<script lang="ts" setup>
  import { computed, onMounted, ref, toRefs } from 'vue'

  import type { TimeTrackingJsonldDefaultRead, TimeTrackingStampJsonldDefaultRead } from '@/api'
  import { useGeneratedTimeTrackingStampStore } from '@/stores'
  import BreakIcon from 'vue-material-design-icons/CoffeeOutline.vue'
  import WorkIcon from 'vue-material-design-icons/BriefcaseClockOutline.vue'
  import DriveIcon from 'vue-material-design-icons/CarOutline.vue'
  import { DateTime, Duration } from 'luxon'
  import { useFormatter } from '@/composables/useFormatter.ts'
  import PageLoading from '@/components/PageLoading.vue'

  const $formatter = useFormatter()

  interface Props {
    index?: number | undefined
    track: TimeTrackingJsonldDefaultRead
  }

  const props = defineProps<Props>()
  const { index, track } = toRefs(props)
  const now = ref(DateTime.now())
  setInterval(() => (now.value = DateTime.now()), 1000)

  const timeTrackingStampStore = useGeneratedTimeTrackingStampStore()
  const lastStamp = ref<TimeTrackingStampJsonldDefaultRead>()

  const liveDuration = computed(() => {
    let res: number | undefined = undefined
    if (lastStamp.value) {
      if (track.value.running) {
        const runtimeWithoutRunning = Duration.fromISO(track.value.totalDuration!).shiftTo('seconds').seconds
        const runtimeRunningOnly = now.value.diff(DateTime.fromISO(lastStamp.value.timeStart!), 'seconds').seconds
        res = runtimeWithoutRunning + runtimeRunningOnly
      } else {
        res = Duration.fromISO(track.value.totalDuration!).shiftTo('seconds').seconds
      }
    } else {
    }
    return res ? $formatter.Time(res, { withSeconds: true, suppressDays: true }) : undefined
  })

  const currentState = computed(() => {
    if (!track.value.running) return undefined
    if (lastStamp.value?.running) {
      return lastStamp.value.type
    }
    return undefined //should never be reached
  })

  const isLoading = ref(true)
  onMounted(() => {
    if (track.value.lastStamp)
      timeTrackingStampStore.fetchByIri(track.value.lastStamp).then((res) => {
        lastStamp.value = res.value
        isLoading.value = false
      })
  })
</script>

<template>
  <div>
    <PageLoading v-if="isLoading" />
    <div
      v-else
      class="flex flex-row items-center">
      <span
        v-if="track.running"
        v-tooltip="{ value: $t(`timetrackingstamp.type.${currentState}`), modifiers: { top: true } }"
        class="mr-2">
        <BreakIcon
          v-if="currentState === 'break'"
          :size="24" />
        <DriveIcon
          v-else-if="currentState === 'drive'"
          :size="24" />
        <WorkIcon
          v-else-if="currentState === 'time'"
          :size="24" />
        <ProgressBar
          class="mt-1"
          mode="indeterminate"
          style="height: 4px" />
      </span>
      <div class="flex flex-col">
        <span
          v-if="$titrop.showIds"
          class="pr-2 text-xs">
          #{{ track.id }}
        </span>
        <span v-if="index === undefined">{{ track.title }}</span>
        <span v-else>{{ index + 1 }}. {{ track.title }}</span>
        <span class="text-xs">Gestartet: {{ $formatter.DateTime(track.createdAt!) }}</span>
        <span
          v-if="track.running"
          class="text-xs">
          Laufzeit: {{ liveDuration }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
